import type { StructuredCollectionConfig } from '@ltvco/refresh-lib/hooks';

/**
 * Collections used by the Military Records.
 *
 * IMPORTANT:
 * - The order of this list must match the order defined in Remoulade.
 * - Do not reorder existing collections, as the position is used to maintain
 *   consistency with Remoulade mappings and processing logic.
 * - When adding a new collection, append it to the end of the array.
 * - Existing entries should remain in their current positions.
 */
export const MILITARY_INDEX_COLLECTIONS: StructuredCollectionConfig[] = [
  {
    id: '0000002',
    plainName: 'United States, Military Veteran Burials, 1775-2018',
    type: 'military_index',
    source: 'militaryRecordsSearch',
    reportType: 'military_indexes',
    ga4ReportType: 'us_military_veteran_burials',
    country: 'United States',
    yearStart: 1775,
    yearEnd: 2018,
    events: [
      {
        name: 'Burial',
        isRange: false,
      },
    ],
    // Primary event is Death; birth lands in event_secondary_2 via extraFields.
    // event_secondary is the Burial event date (from the events override below),
    // which overwrites the birth value that extraFields sets there by default.
    overrides: [
      {
        source: 'deathDate',
        target: 'event_primary_date_full',
        modifiers: [{ name: 'dateToDateRange', args: { monthOptional: true } }],
      },
      {
        source: 'events',
        target: 'event_secondary_date_full',
        modifiers: [
          { name: 'findEvent', args: { event: 'Burial' } },
          { name: 'eventToDateRange' },
        ],
      },
    ],
    omit: ['event_secondary_location_level_3'],
    isV2: true,
  },
  {
    id: '0000003',
    plainName: 'United States, World War 2, Army Enlistment Records, 1938-1946',
    type: 'military_index',
    source: 'militaryRecordsSearch',
    reportType: 'military_indexes',
    ga4ReportType: 'us_world_war_2_army_enlistment_records',
    country: 'United States',
    yearStart: 1938,
    yearEnd: 1946,
    events: [
      {
        name: 'Military Enlistment',
        isRange: true,
        display: 'Military Service',
      },
    ],
    overrides: [
      {
        source: 'events',
        target: 'event_primary_date_full',
        modifiers: [
          { name: 'findEvent', args: { event: 'Military Enlistment' } },
          { name: 'eventToDateRange' },
        ],
      },
    ],
    isV2: true,
  },
  {
    id: '0000126',
    plainName:
      'United States, Department of Veteran Affairs, Beneficiary Identification Records Locator System (BIRLS), 1800-2024',
    type: 'military_index',
    source: 'militaryRecordsSearch',
    reportType: 'military_indexes',
    ga4ReportType:
      'us_department_of_veterans_affairs_beneficiary_identification_records_locator_system_birls',
    yearStart: 1800,
    yearEnd: 2024,
    // Primary event is Death. Birth -> secondary via extraFields.
    overrides: [
      {
        source: 'deathDate',
        target: 'event_primary_date_full',
        modifiers: [{ name: 'dateToDateRange' }],
      },
    ],
    isV2: true,
  },
  {
    id: '0000027',
    plainName: 'United States, Civil War, Soldiers and Sailors, 1861-1865',
    type: 'military_index',
    source: 'militaryRecordsSearch',
    reportType: 'military_indexes',
    ga4ReportType: 'us_civil_war_soldiers_and_sailors',
    yearStart: 1861,
    yearEnd: 1865,
    // No separate middle-name field; no searchable birth/death dates. Primary
    options: { combineFirstMiddleName: true },
    events: [
      {
        name: 'Military Enlistment',
        isRange: true,
        display: 'Military Service',
      },
    ],
    overrides: [
      {
        source: 'events',
        target: 'event_primary_date_full',
        modifiers: [
          { name: 'findEvent', args: { event: 'Military Enlistment' } },
          { name: 'eventToDateRange' },
        ],
      },
    ],
    isV2: true,
  },
];
